import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import { useAPI } from "../../hooks/useAPI";

// ── Queries ───────────────────────────────────────────────────────────────────

export function useSkillsAcademyPrograms(filters = {}) {
  const { authToken } = useAuth();
  const { callAPI } = useAPI();

  return useQuery({
    queryKey: ["skills_academy", "programs", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.set("search", filters.search);
      // categories is an array — append each as a separate ?category= param
      (filters.categories ?? []).forEach((slug) =>
        params.append("category", slug),
      );
      if (filters.level) params.set("level", filters.level);
      if (filters.sort) params.set("sort", filters.sort);
      const qs = params.toString();
      const data = await callAPI(`skills_academy${qs ? `?${qs}` : ""}`, {
        token: authToken,
      });
      return {
        stats: data.stats ?? {
          program_count: 0,
          simulation_count: 0,
          learner_count: 0,
        },
        count: data.count ?? 0,
        results: data.results ?? [],
      };
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!authToken,
  });
}

export function useSkillsAcademyCategories() {
  const { authToken } = useAuth();
  const { callAPI } = useAPI();

  return useQuery({
    queryKey: ["skills_academy", "categories"],
    queryFn: () => callAPI("skills_academy/categories", { token: authToken }),
    staleTime: 30 * 60 * 1000,
    enabled: !!authToken,
  });
}

export function useSkillsAcademyProgramDetail(programId) {
  const { authToken } = useAuth();
  const { callAPI } = useAPI();

  return useQuery({
    queryKey: ["skills_academy", "program", programId],
    queryFn: async () => {
      const data = await callAPI(`skills_academy/${programId}`, {
        token: authToken,
      });
      return {
        ...data,
        enrollmentRequestStatus: data.enrollment_request_status ?? null,
        enrollmentStatus: data.enrollment_status ?? null,
      };
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!authToken && !!programId,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function useStartSkillsAcademyDemoSimulation() {
  const { authToken } = useAuth();
  const { callAPI } = useAPI();

  return useMutation({
    mutationFn: (programId) =>
      callAPI("start_skills_academy_demo_simulation", {
        method: "POST",
        token: authToken,
        body: { program_id: programId },
      }),
  });
}

export function useCreateSkillsAcademyEnrollmentRequest() {
  const { authToken } = useAuth();
  const { callAPI } = useAPI();

  return useMutation({
    mutationFn: ({ programId, unlockFull = false }) =>
      callAPI("skills_academy/enroll_request", {
        method: "POST",
        token: authToken,
        body: { program_id: programId, ...(unlockFull && { unlock_full: true }) },
      }),
  });
}

// ── Subscription helpers ──────────────────────────────────────────────────────

export function useIsSubscribed(programId) {
  const { loggedInUser } = useAuth();
  const programs = loggedInUser?.subscriptions?.training_programs;
  return (
    Array.isArray(programs) &&
    programs.some((p) => String(p.id) === String(programId))
  );
}

// Maps UI sort label to API sort param
export const SORT_TO_API = {
  "Most popular": "popular",
  "Highest rated": "popular",
  Newest: "newest",
};
